/**
 * useFormr Hook - React integration for Formr
 * 
 * CHANGE HISTORY:
 * - 251102_1146 [pending] danielbeer: Moved from Formr to Interfacr
 * - 251023_1954 [pending] danielbeer: Initial creation
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { FormrManager, getFormrInstance } from '@opcentral/formr';
import type { FormrConfig, FormrEvents, Form } from '@opcentral/formr';

interface FormrContextValue {
  formr: FormrManager;
}

const FormrContext = createContext<FormrContextValue | null>(null);

export interface FormrProviderProps {
  config?: FormrConfig;
  events?: FormrEvents;
  children: ReactNode;
}

export function FormrProvider({ config, events, children }: FormrProviderProps) {
  const [formr] = useState(() => getFormrInstance(config, events));
  
  return (
    <FormrContext.Provider value={{ formr }}>
      {children}
    </FormrContext.Provider>
  );
}

export function useFormr(): FormrManager {
  const context = useContext(FormrContext);
  if (!context) {
    throw new Error('useFormr must be used within FormrProvider');
  }
  return context.formr;
}

export function useForm(formId: string) {
  const formr = useFormr();
  const [form, setForm] = useState<Form | undefined>(() => formr.getForm(formId));
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Subscribe to form changes
  useEffect(() => {
    const interval = setInterval(() => {
      const updatedForm = formr.getForm(formId);
      if (updatedForm) {
        setForm({ ...updatedForm });
      }
    }, 100);
    
    return () => clearInterval(interval);
  }, [formr, formId]);
  
  const setFieldValue = (fieldId: string, value) => {
    formr.setFieldValue(formId, fieldId, value);
    setForm({ ...formr.getForm(formId)! });
  };
  
  const validate = () => {
    const errors = formr.validateForm(formId);
    setForm({ ...formr.getForm(formId)! });
    return errors;
  };
  
  const submit = async () => {
    setIsSubmitting(true);
    try {
      const result = await formr.submitForm(formId);
      setForm({ ...formr.getForm(formId)! });
      return result;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const reset = () => {
    if (form) {
      form.data = {};
      form.errors = {};
      form.touched = {};
      form.isValid = false;
      setForm({ ...form });
    }
  };
  
  return {
    form,
    setFieldValue,
    validate,
    submit,
    reset,
    isSubmitting: isSubmitting || form?.isSubmitting || false
  };
}
